import React, { ChangeEvent, FC, KeyboardEvent, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import { AgentNetTextInput, CurrencyField } from 'ui-kit/inputs';
import { Box, Grid, IconButton, Tooltip, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useHistory } from 'react-router-dom';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { AgentNet_Admin } from 'utilities/constants/activity-rights';
import { WIRE_VALIDATION_PRODUCT_CODE } from 'features/help/constants';
import { FieldValidationError } from 'utilities/validation/validation';
import { AgentNetDivider } from 'ui-kit/components/dividers';
import RadioGroup from 'ui-kit/components/radios/RadioGroup';
import { HelpOutlineOutlined } from '@material-ui/icons';
import { wireVerificationSchema } from 'utilities/validation/schemas';
import { doValidate } from 'utilities/validation/validation';
import DrawerFileSection from '../identityVerification/DrawerFileSection';
import { getUnderwriterAlert } from '../identityVerification/underwriterAlertMessage';
import { useFirmUnderwriterOptions } from '../identityVerification/useFirmUnderwriterOptions';
import { useUnderwriterProductStatus } from '../identityVerification/useUnderwriterProductStatus';
import { WireValidationSubmitRequest, WireValidationSubmitResponse } from './interfaces/wireValidation';
import { getPayeeOrBankName } from './wireValidation.config';
import WireVerificationResultView from './WireVerificationResultView';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { AsyncStatus } from 'features/files/fincen-reporting/constants';
import { WireType } from './interfaces/wireValidation';
import { wireVerificationHelpText } from 'features/constants';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import PDFIcon from 'ui-kit/icons/PDF';

interface WireVerificationFormData {
  underwriterCode: string;
  fileNumber: string;
  wireType: string;
  firstName: string;
  lastName: string;
  receivingBankName: string;
  wireAmount: number;
  routingNumber: string;
  firmId: string;
  payeeName: string;
}

interface WireVerificationDrawerProps {
  value?: Partial<WireVerificationFormData>;
  openDrawer: boolean;
  closeDrawer: () => void;
  profileFirmId?: string;
  onSubmitValidation?: (payload: WireValidationSubmitRequest) => Promise<void>;
  submitStatus?: string;
  submitResult?: WireValidationSubmitResponse | null;
  onCopyCreateNew?: (formData: Partial<WireVerificationFormData>) => void;
  orderDetailStatus?: string;
  hasWireValidationRights?: boolean;
  onViewReport?: (fileId: string, orderId: string, payeeOrBankName: string) => void;
  loadingReportOrderId?: string | null;
}

const wireTypeMode = [
  { label: 'Personal', value: WireType.PERSONAL, dataTestId: 'new-wire-validation-radio-personal' },
  { label: 'Business', value: WireType.BUSINESS, dataTestId: 'new-wire-validation-radio-business' },
  {
    label: 'Mortgage Payoff',
    value: WireType.MORTGAGE_PAYOFF,
    dataTestId: 'new-wire-validation-radio-payoff',
  },
];

const VERIFY_NEW_WIRE = 'Verify New Wire';

const defaultValue = { wireType: wireTypeMode[0].value };

const useStyles = makeStyles(() =>
  createStyles({
    tooltipPreWrap: {
      whiteSpace: 'pre-wrap',
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      width: '100%',
    },
    // Reserves room for the alert that is pinned above the drawer actions.
    alertContainer: {
      marginBottom: '62px',
      width: '100%',
    },
    alertMsg: {
      width: '-webkit-fill-available !important',
      maxWidth: 'unset !important',
      position: 'absolute',
      bottom: '49px',
      left: 0,
      padding: '6px 24px',
    },
    alertActionLabel: {
      fontFamily: 'Inter',
      fontSize: pxToRem(12),
      fontWeight: 600,
      lineHeight: pxToRem(22),
      letterSpacing: '0.46px',
      color: 'inherit',
    },
  }),
);

const WireVerificationDrawer: FC<WireVerificationDrawerProps> = ({
  value,
  openDrawer,
  closeDrawer,
  profileFirmId,
  onSubmitValidation,
  submitStatus,
  submitResult,
  onCopyCreateNew,
  orderDetailStatus,
  hasWireValidationRights,
  onViewReport,
  loadingReportOrderId,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { profile } = profileCtx;
  const {
    options: firmUnderwriterOptions,
    isLoading: isLoadingUnderwriters,
    isNonFaUnderwriter,
  } = useFirmUnderwriterOptions(openDrawer);
  const [showAllValidation, setShowAllValidation] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [drawerValue, setDrawerValue] = useState<Partial<WireVerificationFormData>>(value || defaultValue);
  const [showResultView, setShowResultView] = useState<boolean>(false);
  const { addSnackbarMessage } = useSnackBars();

  const selectedWireTypeMode = useMemo(
    () => wireTypeMode.find((mode) => mode.value === drawerValue?.wireType) || wireTypeMode[0],
    [drawerValue?.wireType],
  );

  const { isFeatureEnabled, productStatus } = useUnderwriterProductStatus(drawerValue?.underwriterCode, openDrawer);

  const isAgentNetAdmin = useMemo(() => {
    return profile?.activityRights?.some((rights) => rights.ActivityRightId === AgentNet_Admin);
  }, [profile?.activityRights]);

  // Enablement follows the selected underwriter. The drawer only opens when the firm has the
  // product, so it stays open until an underwriter narrows it down.
  const isWireValidationEnabled =
    !isFeatureEnabled || !drawerValue?.underwriterCode ? true : productStatus?.wireValidation ?? true;

  const underwriterAlert = useMemo(() => {
    if (!isFeatureEnabled) return null;

    return getUnderwriterAlert({
      productName: 'Wire Validation',
      isEnabled: isWireValidationEnabled,
      isNonFaUnderwriter: isNonFaUnderwriter(drawerValue?.underwriterCode),
      isAgentNetAdmin: !!isAgentNetAdmin,
    });
  }, [isFeatureEnabled, isWireValidationEnabled, drawerValue?.underwriterCode, isAgentNetAdmin, isNonFaUnderwriter]);

  const areFieldsDisabled = !isWireValidationEnabled;

  useEffect(() => {
    const newValue = value || defaultValue;
    setDrawerValue(newValue);
  }, [value]);

  const validateForm = async (formData: Partial<WireVerificationFormData>) => {
    const errs: FieldValidationError[] = (await doValidate(formData, wireVerificationSchema)) || [];
    setValidationErrors(errs);
    return errs;
  };

  useEffect(() => {
    validateForm(drawerValue);
  }, []);

  // An underwriter removed from the firm after a validation was saved is no longer selectable, so the
  // prefilled code is dropped instead of leaving the form disabled behind a warning for a stale value.
  useEffect(() => {
    const underwriterCode = drawerValue?.underwriterCode;
    if (!firmUnderwriterOptions || !underwriterCode) return;
    if (firmUnderwriterOptions.some((option) => option.value === underwriterCode)) return;

    const clearedValue = { ...drawerValue, underwriterCode: '' };
    setDrawerValue(clearedValue);
    validateForm(clearedValue);
  }, [firmUnderwriterOptions, drawerValue?.underwriterCode]);

  const handleFieldChange = async (field: keyof WireVerificationFormData, fieldValue: string | number) => {
    const updatedValue = { ...drawerValue, [field]: fieldValue };
    setDrawerValue(updatedValue);
    await validateForm(updatedValue);
  };

  const handleNumericKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key.length === 1 && !/[0-9]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if ((submitStatus === AsyncStatus.SUCCESS || submitResult?.wireValidation?.transactionId) && submitResult) {
      setShowResultView(true);
    }
  }, [submitStatus, submitResult]);

  const buildSubmitPayload = (formData: Partial<WireVerificationFormData>): WireValidationSubmitRequest => {
    const payload: WireValidationSubmitRequest = {
      firmId: Number(profileFirmId) || 0,
      fileNumber: formData.fileNumber ?? '',
      underwriter: formData.underwriterCode ?? '',
      wireType: formData.wireType ?? '',
      bankName: formData.receivingBankName ?? '',
      wireAmount: Number(formData.wireAmount) || 0,
      routingNumber: formData.routingNumber ?? '',
      accountNumber: formData.firmId ?? '',
    };

    if (formData.wireType === WireType.BUSINESS) {
      payload.businessName = formData.payeeName ?? '';
    } else if (formData.wireType === WireType.PERSONAL) {
      payload.firstName = formData.firstName ?? '';
      payload.lastName = formData.lastName ?? '';
    }

    return payload;
  };

  const onSubmit = async () => {
    setShowAllValidation(true);
    const errs = await validateForm(drawerValue);

    if (errs.length > 0) {
      addSnackbarMessage({
        title: `Validation failed`,
        type: 'error',
      });
      return;
    }

    const finalSubmitValue = { ...drawerValue };

    if (selectedWireTypeMode.value === WireType.BUSINESS) {
      delete finalSubmitValue.firstName;
      delete finalSubmitValue.lastName;
    } else if (selectedWireTypeMode.value === WireType.MORTGAGE_PAYOFF) {
      delete finalSubmitValue.firstName;
      delete finalSubmitValue.lastName;
      delete finalSubmitValue.payeeName;
    } else if (selectedWireTypeMode.value === WireType.PERSONAL) {
      delete finalSubmitValue.payeeName;
    }

    if (onSubmitValidation) {
      const payload = buildSubmitPayload(finalSubmitValue);
      await onSubmitValidation(payload);
    }
  };

  const handleDrawerClose = () => {
    setShowAllValidation(false);
    setShowResultView(false);
    setValidationErrors([]);
    setDrawerValue(defaultValue);
    closeDrawer();
  };

  const handleCopyCreateNew = () => {
    setShowResultView(false);

    let prefilled: Partial<WireVerificationFormData>;

    if (submitResult) {
      prefilled = {
        underwriterCode: submitResult.underwriter,
        fileNumber: submitResult.fileNumber,
        wireType: submitResult.wireType,
        firstName: submitResult.firstName,
        lastName: submitResult.lastName,
        receivingBankName: submitResult.bankName,
        wireAmount: submitResult.wireAmount,
        routingNumber: submitResult.routingNumber,
        payeeName: submitResult.payee,
        firmId: '',
      };
    } else {
      prefilled = { ...drawerValue, firmId: '' };
    }

    setDrawerValue(prefilled);
    if (onCopyCreateNew) {
      onCopyCreateNew(prefilled);
    }
  };

  const handleFileSectionChange = async (fileSectionValue: { underwriterCode?: string; fileNumber?: string }) => {
    const updatedValue = {
      ...drawerValue,
      ...fileSectionValue,
    };
    setDrawerValue(updatedValue);
    await validateForm(updatedValue);
  };

  const resultUnderwriterName = useMemo(
    () => firmUnderwriterOptions?.find((option) => option.value === submitResult?.underwriter)?.name,
    [firmUnderwriterOptions, submitResult?.underwriter],
  );

  const isSubmitting = submitStatus === AsyncStatus.PENDING;
  const isLoadingOrderDetail = orderDetailStatus === AsyncStatus.PENDING;

  const getResultTitle = () => {
    if (!submitResult) return '';
    const isMortgagePayoff = submitResult.wireType === WireType.MORTGAGE_PAYOFF;
    const name = isMortgagePayoff ? '' : submitResult.payee;
    return `Wire Validation: ${submitResult.bankName}${name ? `-${name}` : ''}`;
  };

  const drawerTitle = showResultView && submitResult ? getResultTitle() : VERIFY_NEW_WIRE;

  const reportFileId = submitResult?.wireValidation?.fileId;
  const reportOrderId = submitResult?.wireValidation?.transactionId;
  const isReportLoading = !!reportOrderId && loadingReportOrderId === reportOrderId;

  const reportActionItem: ReactNode =
    showResultView && reportFileId && reportOrderId ? (
      <AgentNetButton
        color="primary"
        variant="outlined"
        size="small"
        id="report-action"
        startIcon={<PDFIcon fontSize="small" />}
        loading={isReportLoading}
        disabled={isReportLoading}
        onClick={() =>
          onViewReport?.(
            reportFileId,
            reportOrderId,
            getPayeeOrBankName(submitResult?.wireType, submitResult?.payee, submitResult?.bankName),
          )
        }
        data-testid="wire-validation-button-report"
      >
        Wire Validation Report
      </AgentNetButton>
    ) : undefined;

  return (
    <FormDrawerComponent
      title={isLoadingOrderDetail ? '' : drawerTitle}
      id="wire-verification-drawer-body"
      open={openDrawer}
      width={960}
      disableEnforceFocus
      crossQAPrefix="new-wire-validation-button-"
      onDismissAction={handleDrawerClose}
      onPrimaryAction={showResultView ? handleCopyCreateNew : onSubmit}
      primaryActionLabel={showResultView ? 'Copy & Create New' : 'Submit Validation'}
      primaryActionDataQa={
        showResultView ? 'wire-validation-button-copy-create-new' : 'new-wire-validation-button-submit'
      }
      dismissActionProps={{ 'data-testid': 'new-wire-validation-button-cancel', disabled: isSubmitting }}
      hideDismissAction={drawerTitle !== VERIFY_NEW_WIRE || isLoadingOrderDetail}
      hidePrimaryAction={isLoadingOrderDetail}
      actionItems={isLoadingOrderDetail ? undefined : reportActionItem}
      primaryActionProps={{
        loading: isSubmitting,
        disabled:
          (showResultView && !hasWireValidationRights) ||
          isLoadingOrderDetail ||
          isSubmitting ||
          (!showResultView && areFieldsDisabled),
      }}
    >
      {isLoadingOrderDetail ? (
        <div className={classes.loadingContainer}>
          <LoadingSpinner status="pending" />
        </div>
      ) : showResultView && submitResult ? (
        <WireVerificationResultView result={submitResult} underwriterName={resultUnderwriterName} />
      ) : (
        <>
          <DrawerFileSection
            value={drawerValue}
            onChange={handleFileSectionChange}
            showAllValidation={showAllValidation}
            validationErrors={validationErrors}
            showPersonalIdForm={false}
            qaAttrPrefix="new-wire-validation"
            underwriterOptions={firmUnderwriterOptions}
            isLoadingUnderwriters={isLoadingUnderwriters}
          />
          <Grid container spacing={3}>
            <Grid item sm={12}>
              <AgentNetDivider typoVariant="h3" title={'Wire Type'} disablePadding />
            </Grid>
            <Box mb={1} ml={1.5}>
              <RadioGroup
                selected={selectedWireTypeMode}
                options={wireTypeMode}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const selectedOption = wireTypeMode.find((option) => option.value === e.target.value);
                  if (selectedOption) {
                    handleFieldChange('wireType', selectedOption.value);
                  }
                }}
                disabled={areFieldsDisabled}
              />
              <Tooltip title={<Box className={classes.tooltipPreWrap}>{wireVerificationHelpText}</Box>} placement="top">
                <IconButton
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                  aria-label="Help"
                  data-testid="new-wire-validation-icon-help"
                >
                  <HelpOutlineOutlined color="primary" />
                </IconButton>
              </Tooltip>
            </Box>
            {selectedWireTypeMode.value !== WireType.MORTGAGE_PAYOFF && (
              <>
                <Grid item sm={12}>
                  <AgentNetDivider typoVariant="h3" title={'Payee Contact'} disablePadding />
                </Grid>
                {selectedWireTypeMode.value === WireType.BUSINESS ? (
                  <Grid item sm={12}>
                    <AgentNetTextInput
                      fullWidth
                      variant="outlined"
                      required
                      label={'Payee Name'}
                      name="payeeName"
                      value={drawerValue?.payeeName ?? ''}
                      errs={validationErrors}
                      showValidation={showAllValidation}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChange('payeeName', e.target.value)}
                      disabled={areFieldsDisabled}
                      data-testid="new-wire-validation-input-payee-name"
                    />
                  </Grid>
                ) : (
                  <>
                    <Grid item sm={6}>
                      <AgentNetTextInput
                        fullWidth
                        variant="outlined"
                        required
                        label={'First Name'}
                        name="firstName"
                        value={drawerValue?.firstName ?? ''}
                        errs={validationErrors}
                        showValidation={showAllValidation}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChange('firstName', e.target.value)}
                        disabled={areFieldsDisabled}
                        data-testid="new-wire-validation-input-first-name"
                      />
                    </Grid>
                    <Grid item sm={6}>
                      <AgentNetTextInput
                        fullWidth
                        variant="outlined"
                        required
                        name="lastName"
                        value={drawerValue?.lastName ?? ''}
                        errs={validationErrors}
                        showValidation={showAllValidation}
                        label={'Last Name'}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChange('lastName', e.target.value)}
                        disabled={areFieldsDisabled}
                        data-testid="new-wire-validation-input-last-name"
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
            <Grid item sm={12}>
              <AgentNetDivider typoVariant="h3" title={'Bank Information'} disablePadding />
            </Grid>
            <Grid item sm={6}>
              <AgentNetTextInput
                fullWidth
                variant="outlined"
                required
                label={'Receiving Bank Name'}
                name="receivingBankName"
                value={drawerValue?.receivingBankName ?? ''}
                errs={validationErrors}
                showValidation={showAllValidation}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChange('receivingBankName', e.target.value)}
                disabled={areFieldsDisabled}
                data-testid="new-wire-validation-input-bank-name"
              />
            </Grid>
            <Grid item sm={6}>
              <CurrencyField
                fullWidth
                required
                variant="outlined"
                label={'Wire Amount'}
                id="wireAmount"
                name={'wireAmount'}
                value={drawerValue?.wireAmount ?? ''}
                errs={validationErrors}
                alwaysDisplayError={showAllValidation}
                max={99999999999.99}
                allowNegative={false}
                allowedBlank={true}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChange('wireAmount', e.target.value)}
                disabled={areFieldsDisabled}
                data-testid="new-wire-validation-input-wire-amount"
              />
            </Grid>
            <Grid item sm={6}>
              <AgentNetTextInput
                fullWidth
                variant="outlined"
                required
                label={'Routing Number'}
                name="routingNumber"
                value={drawerValue?.routingNumber ?? ''}
                errs={validationErrors}
                showValidation={showAllValidation}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChange('routingNumber', e.target.value)}
                onKeyDown={handleNumericKeyDown}
                inputProps={{ maxLength: 9 }}
                disabled={areFieldsDisabled}
                data-testid="new-wire-validation-input-routing-number"
              />
            </Grid>
            <Grid item sm={6}>
              <AgentNetTextInput
                fullWidth
                variant="outlined"
                required
                label={'Account Number'}
                name="firmId"
                value={drawerValue?.firmId ?? ''}
                errs={validationErrors}
                showValidation={showAllValidation}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChange('firmId', e.target.value)}
                onKeyDown={handleNumericKeyDown}
                inputProps={{ maxLength: 18 }}
                disabled={areFieldsDisabled}
                data-testid="new-wire-validation-input-account-number"
              />
            </Grid>
          </Grid>
          {underwriterAlert && (
            <>
              <div className={classes.alertContainer} />
              <Alert
                className={classes.alertMsg}
                severity="warning"
                data-testid="new-wire-validation-alert-underwriter"
                {...(underwriterAlert.showEnableAction && {
                  action: (
                    <IconButton
                      aria-label="enable"
                      color="inherit"
                      size="small"
                      onClick={() =>
                        history.push(`/admin/integrations-features?productCode=${WIRE_VALIDATION_PRODUCT_CODE}`)
                      }
                      data-testid="new-wire-validation-button-enable-underwriter"
                    >
                      <Typography className={classes.alertActionLabel}>Enable</Typography>
                    </IconButton>
                  ),
                })}
              >
                {underwriterAlert.message}
              </Alert>
            </>
          )}
        </>
      )}
    </FormDrawerComponent>
  );
};

export default WireVerificationDrawer;
